// WebAudio mixer for radio333. Uploaded slots play when present; synthesized placeholders only for slots
// with no upload. An uploaded slot that fails to load (after one retry) is reported and plays static only.
import { mediaUrl, reportClientError } from "@/lib/api";

const WINDOW = 2; // station audible within ±2
const PHONETIC = "This is a calling station. Three. Three. Three. Sierra. Echo. Charlie. Romeo. Echo. Tango. Dot. Charlie. Oscar. Mike. Repeat.";

export class RadioEngine {
  constructor(cfg, onStationFailed = () => {}) {
    this.cfg = cfg;
    this.onStationFailed = onStationFailed;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(this.ctx.destination);
    this.buffers = {};
    this.voices = {};
    this.freq = null;
    this.speaking = false;
    this._startStatic();
    this._startTuning();
  }

  async _load(slot) {
    if (this.buffers[slot] !== undefined) return this.buffers[slot];
    this.buffers[slot] = null;
    let lastErr;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch(mediaUrl(slot));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.buffers[slot] = await this.ctx.decodeAudioData(await res.arrayBuffer());
        return this.buffers[slot];
      } catch (e) {
        lastErr = e;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 800));
      }
    }
    // Uploaded audio we could not get: callers fall back to static only (never placeholder tones).
    this.buffers[slot] = false;
    reportClientError("radio_audio_load", `${lastErr?.name || "Error"}: ${lastErr?.message || ""}`, slot);
    return false;
  }

  _noiseBuffer(seconds = 2) {
    const b = this.ctx.createBuffer(1, this.ctx.sampleRate * seconds, this.ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return b;
  }

  _loop(buffer, dest) {
    const s = this.ctx.createBufferSource();
    s.buffer = buffer;
    s.loop = true;
    s.connect(dest);
    s.start();
    return s;
  }

  async _startStatic() {
    this.staticGain = this.ctx.createGain();
    this.staticGain.gain.value = 0.35;
    this.staticGain.connect(this.master);
    const buf = this.cfg.has_static ? await this._load("radio_static") : false;
    if (buf) { this._loop(buf, this.staticGain); return; }
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass"; bp.frequency.value = 1800; bp.Q.value = 0.6;
    bp.connect(this.staticGain);
    this._loop(this._noiseBuffer(), bp);
  }

  async _startTuning() {
    this.tuneGain = this.ctx.createGain();
    this.tuneGain.gain.value = 0;
    this.tuneGain.connect(this.master);
    const buf = this.cfg.has_tuning ? await this._load("radio_tuning") : false;
    if (buf) { this._loop(buf, this.tuneGain); return; }
    this.tuneFilter = this.ctx.createBiquadFilter();
    this.tuneFilter.type = "bandpass"; this.tuneFilter.Q.value = 8; this.tuneFilter.frequency.value = 900;
    this.tuneFilter.connect(this.tuneGain);
    this._loop(this._noiseBuffer(), this.tuneFilter);
  }

  moving() {
    const t = this.ctx.currentTime;
    this.tuneGain.gain.cancelScheduledValues(t);
    this.tuneGain.gain.setTargetAtTime(0.25, t, 0.03);
    if (this.tuneFilter) this.tuneFilter.frequency.setTargetAtTime(500 + Math.random() * 2500, t, 0.05);
    clearTimeout(this._stopT);
    this._stopT = setTimeout(() => this.tuneGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.12), 160);
  }

  async _voice(st) {
    if (this.voices[st.freq]) return this.voices[st.freq];
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.connect(this.master);
    const v = { gain, synth: false };
    this.voices[st.freq] = v;
    const buf = st.has_audio ? await this._load(st.slot) : false;
    if (buf) {
      this._loop(buf, gain);
    } else if (st.has_audio) {
      v.failed = true; // uploaded but unreachable: this frequency plays static only
      this.onStationFailed(st.freq);
      if (this.freq !== null) this.setFrequency(this.freq); // re-level static now, not on the next turn
    } else if (st.freq === this.cfg.broadcast_freq) {
      v.synth = true;
      const o = this.ctx.createOscillator();
      o.type = "sine"; o.frequency.value = 333;
      const og = this.ctx.createGain(); og.gain.value = 0.05;
      o.connect(og); og.connect(gain); o.start();
    } else {
      const o = this.ctx.createOscillator();
      const types = ["sawtooth", "square", "triangle", "sine"];
      o.type = types[st.freq % 4];
      o.frequency.value = 140 + (st.freq % 9) * 55;
      const lfo = this.ctx.createOscillator();
      lfo.frequency.value = 2 + (st.freq % 5);
      const lfoGain = this.ctx.createGain(); lfoGain.gain.value = 40;
      lfo.connect(lfoGain); lfoGain.connect(o.frequency); lfo.start();
      const og = this.ctx.createGain(); og.gain.value = 0.08;
      o.connect(og); og.connect(gain); o.start();
    }
    return v;
  }

  _speak(on) {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (on && !this.speaking) {
      this.speaking = true;
      const say = () => {
        if (!this.speaking) return;
        const u = new SpeechSynthesisUtterance(PHONETIC);
        u.rate = 0.78; u.pitch = 0.6; u.volume = 1;
        u.onend = () => { this._speakT = setTimeout(say, 900); };
        synth.speak(u);
      };
      say();
    } else if (!on && this.speaking) {
      this.speaking = false;
      clearTimeout(this._speakT);
      synth.cancel();
    }
  }

  click() {
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "square"; o.frequency.value = 1400;
    g.gain.setValueAtTime(0.15, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.05);
    o.connect(g); g.connect(this.master);
    o.start(); o.stop(this.ctx.currentTime + 0.06);
  }

  async setFrequency(f) {
    this.freq = f;
    const t = this.ctx.currentTime;
    let nearest = Infinity;
    for (const st of this.cfg.stations) {
      const d = Math.abs(st.freq - f);
      if (d <= WINDOW + 4) this._voice(st);
      const v = this.voices[st.freq];
      const level = d <= WINDOW ? 1 - d / (WINDOW + 1) : 0;
      if (v) v.gain.gain.setTargetAtTime(level, t, 0.08);
      if (!v?.failed) nearest = Math.min(nearest, d);
    }
    const atBroadcast = f === this.cfg.broadcast_freq;
    const broadcastFailed = this.voices[this.cfg.broadcast_freq]?.failed;
    const staticLevel = atBroadcast && !broadcastFailed ? 0.05 : nearest <= WINDOW ? 0.12 + 0.23 * (nearest / (WINDOW + 1)) : 0.35;
    this.staticGain?.gain.setTargetAtTime(staticLevel, t, 0.1);
    // Spoken placeholder only when the broadcast slot has no upload at all.
    const synthBroadcast = !this.cfg.stations.find((s) => s.freq === this.cfg.broadcast_freq)?.has_audio;
    this._speak(atBroadcast && synthBroadcast);
  }

  close() {
    this._speak(false);
    this.ctx.close();
  }
}
